import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import styles from './TextColorMenu.module.scss';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';
import { useRecentColors } from '../../hooks/useRecentColors';

// Notion-ish palette: neutrals first row, accents second row.
const PALETTE = [
    '#37352f', // default near-black
    '#787774', // gray
    '#9b9a97', // light gray
    '#ffffff', // white
    '#cb912f', // brown/amber
    '#d9730d', // orange
    '#dfab01', // yellow
    '#448361', // green
    '#337ea9', // blue
    '#9065b0', // purple
    '#c14c8a', // pink
    '#d44c47', // red
];

export function TextColorMenu() {
    const editor = useEditorRef();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    // True while the native OS color-picker overlay (`<input type="color">`)
    // is presumed open. Held in a ref so the outside-click listener reads
    // the latest value without having to re-attach when it toggles. See the
    // `<input>` handlers below for the lifecycle.
    const pickerActiveRef = useRef(false);
    // Holds the latest colour the user has picked inside the OS picker
    // *during* a session. Pushed to recents on blur (picker close) so the
    // Recent strip only records the final colour the user committed to —
    // not every wheel-click made while exploring.
    const pendingColorRef = useRef<string | null>(null);
    const [recents, pushRecent] = useRecentColors('plateEditor.recentColors.text');

    const currentColor: string | undefined = useEditorSelector((ed: any) => ed?.api?.marks?.()?.color, []);

    // Hide recents that duplicate the static palette so the secondary row
    // doesn't waste slots on colors the user can already pick directly.
    const visibleRecents = useMemo(() => {
        const paletteSet = new Set(PALETTE.map((c) => c.toLowerCase()));
        return recents.filter((c) => !paletteSet.has(c.toLowerCase()));
    }, [recents]);

    useEffect(() => {
        if (!open) return;
        function handleClickOutside(e: any) {
            // While the OS color picker is open, clicks on its sliders /
            // gradient swatch sometimes bubble back to `document` with a
            // target outside `wrapRef` (the picker dialog renders in its
            // own surface). Treat those as "still inside" so the menu
            // doesn't slam shut mid-interaction.
            if (pickerActiveRef.current) return;
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        function handleEsc(e: any) {
            if (e.key === 'Escape') setOpen(false);
        }
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [open]);

    const pick = (color: string | null) => (e: React.MouseEvent) => {
        e.preventDefault();
        // null clears the mark; otherwise sets it.
        if (color === null) {
            (editor as any).tf?.removeMarks?.('color');
        } else {
            (editor as any).tf?.color?.addMark?.(color);
        }
        setOpen(false);
    };

    const swatchColor = currentColor || '#37352f';

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(toolbarStyles.btn, open && toolbarStyles.btnActive)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    setOpen((o) => !o);
                }}
                title="Text color"
                aria-label="Text color"
                aria-haspopup="true"
                aria-expanded={open}
                style={{ color: swatchColor }}
            >
                <FormatColorTextIcon fontSize="small" />
            </button>
            {open && (
                <div className={styles.popover} role="menu" aria-label="Pick text color">
                    <div className={styles.grid}>
                        {PALETTE.map((color) => (
                            <button
                                key={color}
                                type="button"
                                className={clsx(styles.swatchBtn, currentColor === color && styles.swatchBtnActive)}
                                role="menuitem"
                                onMouseDown={pick(color)}
                                title={color}
                                aria-label={`Color ${color}`}
                                style={{ background: color }}
                            />
                        ))}
                        {/* Native color wheel for custom hex — keyed by a stable label so the
                            chrome appears identical across menus. The wrapping span keeps the
                            input's default circular swatch sized like the palette tiles.
                            Recents-on-blur (not on-change): macOS's native picker fires `change`
                            every time the user clicks a colour in the wheel, so the previous
                            on-change push polluted the Recent strip with every browsed colour.
                            We now only commit the *final* hex on blur (when the picker closes),
                            tracking the latest value in `pendingColorRef` during the session. */}
                        <span className={styles.customWrap} title="Pick a custom color" aria-label="Custom color">
                            <input
                                type="color"
                                value={currentColor || '#000000'}
                                onMouseDown={() => {
                                    // Mark the OS picker as active BEFORE it opens so the
                                    // outside-click listener treats inner clicks as in-scope.
                                    pickerActiveRef.current = true;
                                    pendingColorRef.current = null;
                                }}
                                onInput={(e) => {
                                    // Live preview as the user drags the slider.
                                    const v = (e.target as HTMLInputElement).value;
                                    (editor as any).tf?.color?.addMark?.(v);
                                    pendingColorRef.current = v;
                                }}
                                onChange={(e) => {
                                    (editor as any).tf?.color?.addMark?.(e.target.value);
                                    pendingColorRef.current = e.target.value;
                                }}
                                onBlur={() => {
                                    // Picker dismissed. Push the *final* picked colour to
                                    // recents (skipping all the intermediate clicks the user
                                    // may have made browsing the wheel). Then release the
                                    // outside-click guard.
                                    if (pendingColorRef.current) {
                                        pushRecent(pendingColorRef.current);
                                        pendingColorRef.current = null;
                                    }
                                    setTimeout(() => {
                                        pickerActiveRef.current = false;
                                    }, 0);
                                }}
                                className={styles.customInput}
                            />
                        </span>
                    </div>
                    {visibleRecents.length > 0 && (
                        <div className={styles.recents}>
                            <div className={styles.recentsLabel}>Recent</div>
                            {visibleRecents.map((color) => (
                                <button
                                    key={color}
                                    type="button"
                                    className={clsx(styles.swatchBtn, currentColor === color && styles.swatchBtnActive)}
                                    role="menuitem"
                                    onMouseDown={pick(color)}
                                    title={color}
                                    aria-label={`Recent color ${color}`}
                                    style={{ background: color }}
                                />
                            ))}
                        </div>
                    )}
                    <button type="button" className={styles.clear} role="menuitem" onMouseDown={pick(null)}>
                        Default
                    </button>
                </div>
            )}
        </div>
    );
}
